<template>
  <div class="target-sensor-item-selector">
    <div class="form-container">
      <!-- 主センサー測定項目選択 -->
      <div class="form-specific">
        <p class="label" v-t="'appTargetSensorItemSelector.itemToShow'" />
        <b-form-select
          size="sm"
          v-model="value.main"
          v-bind:options="options"
        />
      </div>
      <!-- 比較対象センサー測定項目表示 -->
      <div class="form-specific">
        <p class="label" v-t="'appTargetSensorItemSelector.itemToCompare'" />
        <b-form-input
          size="sm"
          readonly
          v-bind:disabled="!optionsToCompare.length"
          v-bind:value="sensorItemNameToCompare"
          class="selected-sensor-item-input"
          v-on:click="$_showSelectorModal"
        />
      </div>
    </div>
    <ifield-b-modal
      v-bind:id="modalId"
      v-bind:title="$t('appTargetSensorItemSelector.itemToCompare')"
      v-bind:ok-title="$t('appTargetSensorItemSelector.action.select')"
      v-bind:cancel-title="$t('appTargetSensorItemSelector.action.cancel')"
      body-class="target-sensor-item-selector-dialog-body"
      ok-variant="success"
      size="md"
      centered
      scrollable
      v-on:ok="$_updateValueToCompre"
    >
      <item-selector
        multi
        v-bind:titles="titles"
        v-bind:data="optionsToCompare"
        value-field="value"
        v-model="currentValuesToCompare"
      />
    </ifield-b-modal>
  </div>
</template>

<script>
/**
 * IDを値に結合する
 * @param {Object} value ID値(sensor_id, sensor_item_id)
 * @returns {String} 結合値(ハイフン区切り)
 */
const joinId = (value) => {
  return value ? `${value.sensor_id}-${value.sensor_item_id}` : null
}

/**
 * 値をIDに分解する
 * @param {String} value 結合値(ハイフン区切り)
 * @returns {Object} ID値(sensor_id, sensor_item_id)
 */
const splitValue = (value) => {
  if (!value) return null
  const splitted = value.split('-')
  return { sensor_id: +splitted[0], sensor_item_id: +splitted[1] }
}

export default {
  name: 'AppTargetSensorItemSelector',
  props: {
    /**
     * 選択可能なセンサーデータ
     */
    sensors: {
      type: Array,
      default: () => [],
    },
    /**
     * 主選択値
     */
    valueMain: {
      type: Object,
      default: null,
    },
    /**
     * 比較対象選択値
     */
    valuesToCompare: {
      type: Array,
      default: () => [],
    },
  },
  data: function () {
    return {
      // 内部保持値(sensor_idとsensor_item_idをハイフンで結合した状態で保持)
      value: {
        main: null,
        toCompare: [],
      },
      // モーダル内のカレント値
      currentValuesToCompare: [],
    }
  },
  computed: {
    /**
     * 主選択センサー測定項目の単位
     * * 必然的に比較対象センサー測定項目も同じ単位になる
     * @type {string}
     */
    unit: function () {
      if (!this.value.main) return null
      return this.$_getUnit(this.splittedValue.main.sensor_item_id)
    },
    /**
     * 主選択用センサー測定項目選択肢
     * @type {Array.<Object>}
     */
    options: function () {
      const result = []
      for (const sensor of this.sensors) {
        const name = sensor.sensor_name
        for (const item of sensor.items) {
          result.push({
            value: `${sensor.id}-${item.sensor_item_id}`,
            text: `${name}:${item.sensor_item_name}`,
            unit: this.$_getUnit(item.sensor_item_id),
          })
        }
      }
      return result
    },
    /**
     * 主選択センサー測定項目にコード設定がある場合はtrue
     * @type {Boolean}
     */
    hasCodeSetting: function () {
      if (!this.value.main) return false
      return !!this.$_getValueType(this.splittedValue.main.sensor_item_id)
    },
    /**
     * 比較対象選択用センサー測定項目選択肢
     * @type {Array.<Object>}
     */
    optionsToCompare: function () {
      if (!this.value.main) return []
      // 単位が空 or コード値設定がある場合は比較対象なし
      if (!this.unit || this.hasCodeSetting) return []

      return this.options.filter(
        // 単位が同一かつ主選択と異なるもの
        (opt) => opt.unit === this.unit && opt.value !== this.value.main
      )
    },
    /**
     * 比較対象を選択可能な場合はtrue
     * @type {Boolean}
     */
    canSelectSensorItemsToCompare: function () {
      return !!this.optionsToCompare.length
    },
    /**
     * 比較対象センサー測定項目名
     * @type {String}
     */
    sensorItemNameToCompare: function () {
      return this.value.toCompare
        .map((value) => {
          const option = this.options.find((opt) => opt.value === value)
          return option ? option.text : null
        })
        .filter((v) => v)
        .join(',')
    },
    /**
     * モーダルのID
     * @type {String}
     */
    modalId: function () {
      return 'target-sensor-item-selector-modal'
    },
    /**
     * モーダルの選択コンポーネントに渡すタイトル
     * @type {Array.<Object>}
     */
    titles: function () {
      return [
        {
          key: 'text',
          label: this.$t('appTargetSensorItemSelector.itemToCompare'),
        },
      ]
    },
    /**
     * 分解済み現在値
     * @type {Object}
     */
    splittedValue: function () {
      return {
        main: splitValue(this.value.main),
        toCompare: this.value.toCompare.map((v) => splitValue(v)),
      }
    },
  },
  watch: {
    valueMain: {
      immediate: true,
      handler: function (value) {
        this.value.main = joinId(value)
      },
    },
    valuesToCompare: {
      immediate: true,
      handler: function (values) {
        this.value.toCompare = Array.isArray(values)
          ? values.map((v) => joinId(v))
          : []
      },
    },
    unit: function () {
      this.value.toCompare = []
    },
    splittedValue: function () {
      this.$_emitChangedEvent()
    },
  },
  methods: {
    /**
     * 単位を取得する
     * @param {number} id センサー測定項目ID
     * @returns {stirng} 単位
     */
    $_getUnit: function (id) {
      const item = this.$store.getters['sensorItem/getSensorItemById'](id)
      return item ? item.unit : null
    },

    /**
     * 値タイプを取得する
     * @param {number} id センサー測定項目ID
     * @returns {Array.<Object>} 値タイプ
     */
    $_getValueType: function (id) {
      const item = this.$store.getters['sensorItem/getSensorItemById'](id)
      return item ? item.value_type : null
    },

    /**
     * 選択モーダル表示
     */
    $_showSelectorModal: function () {
      this.currentValuesToCompare = [...this.value.toCompare]
      this.$bvModal.show(this.modalId)
    },

    /**
     * 比較対象センサー測定項目値を更新する
     */
    $_updateValueToCompre: function () {
      this.value.toCompare = [...this.currentValuesToCompare]
    },

    /**
     * 変更イベント発行
     */
    $_emitChangedEvent: function () {
      /**
       * 選択状態変更イベント
       * @param {Object} value 選択状態 (main: Object, toCompare: Array.<Object>)
       */
      this.$emit('changed', this.splittedValue)
    },
  },
}
</script>

<style lang="scss" scoped>
.target-sensor-item-selector {
  width: 100%;
}

.form-container {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  column-gap: 4px;
}

.form-specific {
  width: calc(50% - 2px);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 8px;

  .label {
    font-size: $font-size-small;
    font-weight: $font-weight-bold;
    margin: 0;
    line-height: 1;
  }
}

.selected-sensor-item-input {
  &:not(:disabled) {
    background-color: #ffffff;
    cursor: pointer;
  }
}

::v-deep .target-sensor-item-selector-dialog-body {
  background-color: #ffffff;
}
</style>
